<template>
  <header class="header header--filters">
    <FilterTagsRow
      :filters="allFilterTags"
      :domain="filtersCategory"
      text="Filters"
      @tags-changed="toggleTags"
      @openModal="toggleAllFilters"
    />
    <transition name="fade-in-out">
      <div v-if="isAdditionalFiltersVisible" class="rest-of-filters">
        <!--
        <button class="modal-btn">
          {{ text }}
        </button>
        -->

        <div class="container additional-filters">
          <div class="container-wrapper" ref="wrapper">
            <div class="carousel-slider" ref="carousel">
              <SectionTab
                :value="section.name"
                :isActive="selectedCategory === section.id"
                class="section-tab"
                v-for="section in availableSections"
                :key="section.id"
                @click="onSlectSection(section)"
              />
              <slot name="tabs" />
            </div>
          </div>
          <slot />
        </div>

        <div
          class="tag-cloud"
          :class="{'all-shown': isAllShown, collapsed: availableFilterTags.length}"
          v-if="isTagsSectionVisible || specialTabOpen"
        >
          <span>
            <Tag
              ref="tags"
              v-for="tag in filters"
              :tag="{...tag, isActive: tag.value}"
              :key="tag.id"
              @click="toggleTag(tag)"
              :class="{'tag-withcheckmark': tag.value}"
              :darkSelection="true"
            />
          </span>
          <slot name="special-tab" />
        </div>
      </div>
    </transition>
    <div class="intersectional-filters">
      <!--
      <button class="modal-btn">Filter by</button>
      -->
      <div class="container">
        <div class="container-wrapper">
          <div class="carousel-slider">
            <RatingInput :value="ratingValue" @change="onRatingChange" />
            <ReviewCount :value="reviewValue" @change="onReviewChange" />
            <PriceRatingFilter :value="priceRating" @change="onPriceRatingChange" v-if="isDinning" />
            <DietaryFilter :value="dietaryValue" @change="onDietaryValueChange" v-if="isDinning" />
            <OpenHours :value="openHours" @change="onOpenHoursChange" v-if="isDinning || isExperience" />
            <reservations-filter :value="reservable" @change="onReservableChange" v-if="isDinning" />
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import SectionTab from './section-tab';
import Tag from '@common/filter-tag/tag.vue';
import {computed, ref} from '@vue/composition-api';
import FilterTagsRow from '@common/filter/filter-tags-row/filter-tags-row.vue';
import {UserActivityCategoryEnum} from '@mobi-components/global-solution';
import RatingInput from './rating-input-filter.vue';
import ReviewCount from './review-count-filter.vue';
import PriceRatingFilter from './price-rating-filter.vue';
import DietaryFilter from './dietary-filter.vue';
import OpenHours from './open-hours-filter.vue';
import ReservationsFilter from './reservations-filter.vue';
import {getIntersectionalFilters, loadRecommendationDetails, updateIntersectionFilter} from '@/store/state/helpers';
import {ProblmSubmissionRoute} from '@/store/state/global.types';

export default {
  components: {
    SectionTab,
    Tag,
    FilterTagsRow,
    RatingInput,
    ReviewCount,
    PriceRatingFilter,
    DietaryFilter,
    OpenHours,
    ReservationsFilter,
  },
  props: {
    availableSections: {
      type: Array,
      default: () => [],
    },
    availableFilterTags: {
      type: Array,
      default: () => [],
    },
    allFilterTags: {
      type: Array,
      default: () => [],
    },
    isTagsSectionVisible: {
      type: Boolean,
      default: false,
    },
    selectedCategory: {
      type: String,
      default: '',
    },
    specialTabOpen: {
      type: Boolean,
      default: false,
    },
    filtersCategory: {
      type: String,
      default: '',
    },
    contextName: {
      type: String,
      default: '',
    },
  },
  setup(props, {emit}) {
    const isDinning = computed(() => {
      return props.filtersCategory === UserActivityCategoryEnum.DINING;
    });
    const isExperience = computed(() => {
      return props.filtersCategory === UserActivityCategoryEnum.EXPERIENCE;
    });
    const intersectionalFiltersDomain = computed(() => {
      return getIntersectionalFilters();
    });
    const carousel = ref(null);
    const wrapper = ref(null);
    const tags = ref(null);
    const isAllShown = ref(true);
    const isAdditionalFiltersVisible = ref(false);
    const ratingValue = computed(() => {
      const filters = intersectionalFiltersDomain.value[props.filtersCategory] || [];
      const filter = filters.find((x) => x.title === 'Min Star Rating');
      return filter?.value || null;
    });
    const onRatingChange = async (value) => {
      await updateIntersectionFilter(props.filtersCategory, {title: 'Min Star Rating', value});
      await loadRecommendationDetails(ProblmSubmissionRoute.SOLUTION, props.contextName);
    };
    const reviewValue = computed(() => {
      const filters = intersectionalFiltersDomain.value[props.filtersCategory] || [];
      const filter = filters.find((x) => x.title === 'Min Review Count');
      return filter?.value || null;
    });
    const onReviewChange = async (value) => {
      await updateIntersectionFilter(props.filtersCategory, {title: 'Min Review Count', value});
      await loadRecommendationDetails(ProblmSubmissionRoute.SOLUTION, props.contextName);
    };
    const priceRating = computed(() => {
      const filters = intersectionalFiltersDomain.value[props.filtersCategory] || [];
      const filter = filters.find((x) => x.title === 'Price Rating');
      return filter?.value || null;
    });
    const onPriceRatingChange = async (value) => {
      await updateIntersectionFilter(props.filtersCategory, {title: 'Price Rating', value});
      await loadRecommendationDetails(ProblmSubmissionRoute.SOLUTION, props.contextName);
    };
    const dietaryValue = computed(() => {
      const filters = intersectionalFiltersDomain.value[props.filtersCategory] || [];
      const filter = filters.find((x) => x.title === 'Dietary');
      return filter?.value || null;
    });
    const onDietaryValueChange = async (value) => {
      await updateIntersectionFilter(props.filtersCategory, {title: 'Dietary', value});
      await loadRecommendationDetails(ProblmSubmissionRoute.SOLUTION, props.contextName);
    };
    const openHours = computed(() => {
      const filters = intersectionalFiltersDomain.value[props.filtersCategory] || [];
      const filter = filters.find((x) => x.title === 'Open Hours');
      return filter?.value || null;
    });
    const onOpenHoursChange = async (value) => {
      await updateIntersectionFilter(props.filtersCategory, {title: 'Open Hours', value});
      await loadRecommendationDetails(ProblmSubmissionRoute.SOLUTION, props.contextName);
    };
    const reservable = computed(() => {
      const filters = intersectionalFiltersDomain.value[props.filtersCategory] || [];
      const filter = filters.find((x) => x.title === 'Reservable');
      return filter?.value || null;
    });
    const onReservableChange = async (value) => {
      await updateIntersectionFilter(props.filtersCategory, {title: 'Reservable', value});
      await loadRecommendationDetails(ProblmSubmissionRoute.SOLUTION, props.contextName);
    };

    const onSlectSection = (section) => {
      isAllShown.value = true;
      emit('section-selected', section);
    };
    const toggleTag = (tag) => {
      emit('toggle-tag', tag);
    };

    const getSortedTags = (tags) => {
      return [...tags];
    };

    const filters = computed(() => {
      if (!isAllShown.value) {
        return getSortedTags(props.availableFilterTags.slice(0, 20));
      }

      const rest = getSortedTags(props.availableFilterTags);
      return rest;
    });

    const toggleAllFilters = () => {
      isAdditionalFiltersVisible.value = !isAdditionalFiltersVisible.value;
    };

    const toggleTags = (tags) => {
      tags.forEach((tag) => toggleTag({...tag, value: !tag.value}));
    };

    const text = computed(() => {
      switch (props.filtersCategory) {
        case UserActivityCategoryEnum.ACCOMMODATION:
          return 'Stay';
        case UserActivityCategoryEnum.DINING:
          return 'Dining';
        case UserActivityCategoryEnum.EXPERIENCE:
          return 'Experience';
      }
    });

    return {
      isDinning,
      isExperience,
      text,
      onSlectSection,
      toggleTag,
      carousel,
      wrapper,
      tags,
      isAllShown,
      filters,
      toggleAllFilters,
      toggleTags,
      isAdditionalFiltersVisible,
      ratingValue,
      onRatingChange,
      reviewValue,
      onReviewChange,
      priceRating,
      onPriceRatingChange,
      dietaryValue,
      onDietaryValueChange,
      openHours,
      onOpenHoursChange,
      reservable,
      onReservableChange,
    };
  },
};
</script>

<style scoped lang="scss">
@import '../vue-transitions.scss';

.header {
  flex: 1;
  padding: 0 20px;
  &--filters {
    padding: 0;
  }
}

.container {
  display: flex;
  justify-content: space-between;
  position: relative;
  padding-right: 10px;
  padding-left: 0px;
  width: 100%;
}

.container::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0px;
  bottom: 0;
  width: 50px;
  pointer-events: none;
  /* background: linear-gradient(270deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%); */
}

.container-wrapper {
  display: flex;
  flex-wrap: wrap;
  // overflow: hidden;
  max-height: 54px;
  justify-content: flex-start;
  position: relative;
  overflow-x: auto;
}

.section-tab:first-child {
  margin-left: 10px;
}

.section-tab + .section-tab {
  margin-left: 10px;
}

.tag-cloud {
  position: relative;
  text-align: center;
}

.tag-cloud.collapsed > *:first-child {
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-wrap: wrap;
  padding: 0px 0px 10px 0px;
  height: 50px;
  max-height: 140px;
  overflow: hidden;
}

.tag-cloud.all-shown > *:first-child {
  height: auto;
  overflow: auto;
}

.right {
  position: absolute;
  background-image: url("data:image/svg+xml,%3Csvg width='9' height='11' viewBox='0 0 9 11' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9 5.5L0.75 10.2631L0.75 0.73686L9 5.5Z' fill='%23071D3B' fill-opacity='0.19'/%3E%3C/svg%3E%0A");
  background-repeat: no-repeat;
  background-position: 70% center;
  background-color: #fff;
  cursor: pointer;
  right: 10px;
  top: 0;
  bottom: 0;
  width: 30px;
  z-index: 1;
}

.left {
  position: absolute;
  background-image: url("data:image/svg+xml,%3Csvg width='9' height='11' viewBox='0 0 9 11' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M-2.40413e-07 5.5L8.25 0.73686L8.25 10.2631L-2.40413e-07 5.5Z' fill='%239CA5B0' fill-opacity='0.49'/%3E%3C/svg%3E%0A");
  background-repeat: no-repeat;
  background-position: 30% center;
  background-color: #fff;
  cursor: pointer;
  left: 10px;
  top: 0;
  bottom: 0;
  width: 20px;
  z-index: 1;
}
.carousel-slider {
  transition: transform 0.2s linear;
  display: inline-block;
  display: flex;
}

.show-more-btn {
  font-size: 16px;
  position: relative;
  border: 0;
  appearance: none;
  background-color: #fff;
  color: #a3abb6;
  line-height: 30px;
  height: 26px;
  width: 100%;
  top: -7px;
}

.show-more-btn > span {
  display: flex;
}

.show-more-btn > span::before,
.show-more-btn > span::after {
  content: '';
  top: 14px;
  position: relative;
  flex: 1;
  width: 100%;
  height: 1px;
  background-color: #a3abb6;
  flex: 1;
}
.show-more-btn > span::before {
  margin-right: 30px;
}
.show-more-btn > span::after {
  margin-left: 30px;
}

.duration {
  display: flex;
  padding: 10px;
  flex-wrap: wrap;
}

.rest-of-filters {
  position: relative;
  padding-left: 100px;
}

.modal-btn {
  position: absolute;
  padding: 12px 8px 0 15px;
  left: 0;
  top: 5px;
  text-align: left;
  color: var(--font-color-dark);
  font-weight: bold;
  font-size: 14px;
  border: none;
  background-color: transparent;
  border-radius: 0;
  border-right: 1px solid #f1f1f1;
  width: 95px;
  overflow: hidden;
  appearance: none;
  display: flex;
  bottom: 0;
}

.intersectional-filters {
  position: relative;
  // height: 60px;

  padding-left: 100px;
}

.intersectional-filters .container-wrapper {
  overflow: visible;
  max-height: none;
}

.intersectional-filters .container-wrapper .carousel-slider {
  flex-wrap: wrap;
}

.additional-filters {
  overflow: auto;
}
.tag--tag-cloud {
  font-size: 14px;
  padding: 7px 16px 7px;
  &:after {
    margin: -6px -6px 0 0;
    width: 14px;
    height: 14px;
    background-position: 2px 2px;
  }
}
</style>
